import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// assets
import arrowIcon from "../../../assets/previous_icon.png";
import useLanguage from "../../../utils/useLanguage";
import CustomInputWithUnit from "../../../components/CustomInputWithUnit";
import CustomCheckbox from "../../../components/CustomCheckbox";

const ProductOrderList = ({
  data = [],
  selectedItems = {},
  onItemSelect,
  currency,
  onInputChange,
  allSelectedItems = [],
  headers = [], // Array of header objects: { key, label, type, unit?, component? }
  groupByField = "code", // Field to group items by (e.g., 'code', 'category')
  itemsField = "colors", // Field that contains the items array (e.g., 'colors', 'variants')
  itemKeyField = "colorCode", // Field to use as item key (e.g., 'colorCode', 'id')
  groupLabelPrefix = "code", // Translation key for group label prefix
}) => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");

  const [collapsed, setCollapsed] = useState({});

  /* HANDLER FUNCTIONS */
  const handleCheckboxChange = (groupCode, itemKey) => {
    const isSelected = selectedItems[groupCode]?.[itemKey];
    onItemSelect(groupCode, itemKey, !isSelected);
  };

  const toggleCollapse = (groupCode) => {
    setCollapsed((prev) => ({
      ...prev,
      [groupCode]: !prev[groupCode],
    }));
  };

  // Find values from allSelectedItems
  const getInputValue = (groupCode, itemKey, field) => {
    const group = allSelectedItems.find(
      (item) => item[groupByField] === groupCode
    );
    if (!group) return "";

    const item = group[itemsField].find((i) => i[itemKeyField] === itemKey);
    if (!item) return "";

    return item[field] || "";
  };

  const handleInputValueChange = (groupCode, itemKey, field, value) => {
    if (onInputChange) {
      onInputChange(groupCode, itemKey, field, value);
    }
  };

  // Render field based on header configuration
  const renderField = (header, group, item) => {
    const { key, type, unit, component: CustomComponent } = header;

    if (CustomComponent) {
      return (
        <CustomComponent
          value={getInputValue(group[groupByField], item[itemKeyField], key)}
          onChange={(e) =>
            handleInputValueChange(
              group[groupByField],
              item[itemKeyField],
              key,
              e.target.value
            )
          }
          unit={unit ? t(unit) : undefined}
          currency={currency}
        />
      );
    }

    switch (type) {
      case "checkbox":
        return (
          <CustomCheckbox
            checked={
              selectedItems[group[groupByField]]?.[item[itemKeyField]] || false
            }
            onChange={() =>
              handleCheckboxChange(group[groupByField], item[itemKeyField])
            }
          />
        );

      case "input":
        return (
          <CustomInputWithUnit
            value={getInputValue(group[groupByField], item[itemKeyField], key)}
            onChange={(e) =>
              handleInputValueChange(
                group[groupByField],
                item[itemKeyField],
                key,
                e.target.value
              )
            }
            unit={
              unit
                ? unit === "currency"
                  ? t(currency?.value || "USD")
                  : t(unit)
                : undefined
            }
          />
        );

      case "text":
      default:
        return <span>{item[key]}</span>;
    }
  };

  /* USEEFFECT - Modified to preserve existing collapsed state */
  useEffect(() => {
    setCollapsed((prevCollapsed) => {
      const newCollapsed = { ...prevCollapsed };

      // Only set collapsed state for new groups that don't exist in current state
      data.forEach((group) => {
        const groupKey = group[groupByField];
        if (!(groupKey in newCollapsed)) {
          newCollapsed[groupKey] = true; // Default new groups to collapsed
        }
        // Existing groups keep their current state
      });

      return newCollapsed;
    });
  }, [data, groupByField]);

  return (
    <div
      className="c-fabric-table o-column o-column--gap-size_400 u-margin u-margin--bottom_700"
      dir={currentDirection}
    >
      {data.map((group) => (
        <div key={group[groupByField]} className="c-fabric-block">
          <div
            className="c-fabric-header"
            onClick={() => toggleCollapse(group[groupByField])}
          >
            <span className="c-collapse-icon" style={{ cursor: "pointer" }}>
              <img
                src={arrowIcon}
                alt="Collapse"
                style={{
                  width: 16,
                  transform: collapsed[group[groupByField]]
                    ? "rotate(0deg)"
                    : "rotate(-90deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </span>

            <div className="o-column o-column--gap-size_200">
              <span className="c-fabric-code">
                {t(groupLabelPrefix)}: {group[groupByField]}
              </span>
              {/* <span className="c-fabric-code">
                    {t("uniformName")}: {group[groupByField]}
                  </span> */}

              {group.color && (
                <span className="c-fabric-code">
                  {t("color")}: {group.color}
                </span>
              )}
            </div>
          </div>

          {!collapsed[group[groupByField]] && (
            <table className="c-fabric-inner-table">
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header.key}>{t(header.label)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {group[itemsField]?.map((item) => (
                  <tr key={item[itemKeyField]}>
                    {headers.map((header) => (
                      <td key={header.key}>
                        {renderField(header, group, item)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
};

export default ProductOrderList;
